import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import BusinessIcon from '@mui/icons-material/Business';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormHelperText,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { previousFileVersionInterface } from 'common/interface';
import { FileData } from 'common/utils';
import { userTypes } from 'config/config';
import { popupNotification } from 'notifications/ToastNotification';
import { SubmitHandler, useForm } from 'react-hook-form';
import { LuUser } from 'react-icons/lu';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from 'redux/store';
import { folderNameRegex } from 'screens/superAdmin/validation';
import * as yup from 'yup';

import CustomModal from '../../components/CustomModal';
import CustomDropdown from '../../components/elements/CustomDropdown';
import CustomIconButton from '../../components/elements/IconButton';
import FileTable from '../../components/fileComponent/FileList';
import FileUploadModal from '../../components/fileComponent/FileUpload';
import PreviousVersionFileComp from '../../components/fileComponent/PreviousVersionFileComp';
import TrialPeriodModal from '../../components/TrialPeriodModal';
import { useFileUpload } from '../../hooks/useFileUpload'; // Import the hook
import { useTrialPeriod } from '../../hooks/useTrialPeriod';
import {
  getAllFileVersions,
  getPrivateSharedFiles,
  getUserFiles,
  getUsersByAction,
  handleCreateNewFolder,
} from './adminDashboardService';
// loading states interface
interface loadingStates {
  fetchingFiles: boolean;
  creatingFolder: boolean;
  loadingUsers: boolean;
  fetchingFileVersion: boolean;
}
// Define validation schema using Yup
const schema = yup
  .object({
    option: yup.string().required('This field is required'),
  })
  .required();

interface FormValues {
  option: string;
}
interface Props {
  showUserSelection?: boolean;
}

const AdminDashboard: React.FC<Props> = ({ showUserSelection = true }) => {
  const { showTrialModal, closeTrialModal } = useTrialPeriod();
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {};
  const { uuid, '*': dynamicPath } = useParams();
  const [users, setUsers] = useState([] as any[]);
  const [loading, setLoading] = useState({} as loadingStates);
  const [userFiles, setUserFiles] = useState([] as FileData[]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [showFileVersionModal, setShowFileVersionModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isPrivate, setIsPrivate] = useState(true);
  const [previousFileVersions, setPreviousFileVersions] = useState(
    [] as previousFileVersionInterface[]
  );
  const [selectedFileForVersion, setSelectFileForVersion] =
    useState<FileData>();
  const [path, setPath] = useState<string[]>(
    dynamicPath ? dynamicPath.split('/').filter(Boolean) : []
  );
  const { firstName, lastName, employeeId, role, corporateId } = useSelector(
    (state: RootState) => state.auth
  );
  const isCorporate =
    role === userTypes.corporateAdmin || role === userTypes.corporateStaff
      ? true
      : false;
  const selectedOption = watch('option');
  const newFolderPath = path.join('/');
  const [baseFolder, setBaseFolder] = useState('private-shared');
  const {
    uploadFiles,
    progress,
    isUploading,
    setSelectedFiles,
    clearFiles,
    files,
  } = useFileUpload();
  const handleUpload = async () => {
    // Prevent multiple simultaneous uploads
    if (loading.fetchingFiles) return;

    await uploadFiles(
      uuid,
      selectedOption || corporateId,
      firstName,
      lastName,
      newFolderPath,
      setModalOpen
    );
    // Only refresh if not already loading
    if (!loading.fetchingFiles) {
      getUserFiles(
        selectedOption || corporateId,
        setLoading,
        uuid,
        setUserFiles
      );
    }
    clearFiles(); // Use clearFiles instead of setSelectedFiles([])
  };

  const handlePrivateFileUpload = async () => {
    // Prevent multiple simultaneous uploads
    if (loading.fetchingFiles) return;

    if (
      role === userTypes.corporateAdmin ||
      role === userTypes.corporateStaff
    ) {
      await uploadFiles(
        uuid,
        corporateId,
        firstName,
        lastName,
        newFolderPath,
        setModalOpen,
        'private-shared'
      );
    } else {
      await uploadFiles(
        uuid,
        null,
        firstName,
        lastName,
        newFolderPath,
        setModalOpen,
        'private-shared'
      );
    }
    // Only refresh if not already loading
    if (!loading.fetchingFiles) {
      await getPrivateSharedFiles(
        setLoading,
        setIsPrivate,
        setUserFiles,
        setPath,
        isCorporate
      );
    }
  };

  const getFileVersions = async (file: FileData) => {
    await getAllFileVersions(
      file,
      setSelectFileForVersion,
      setShowFileVersionModal,
      setLoading,
      setPreviousFileVersions
    );
  };

  useEffect(() => {
    // Only make API call if we have a selectedOption and we're not already loading
    if (selectedOption && !loading.fetchingFiles) {
      setUserFiles([] as FileData[]);
      getUserFiles(selectedOption, setLoading, uuid, setUserFiles);
      setPath([]);
      setIsPrivate(false);
      setBaseFolder('Home');
    }
  }, [selectedOption]);

  useEffect(() => {
    // Only make API call for corporate users on mount if we don't have a selectedOption and we're not already loading
    if (
      (role === userTypes.corporateAdmin ||
        role === userTypes.corporateStaff) &&
      !isPrivate &&
      !selectedOption &&
      !loading.fetchingFiles
    ) {
      setUserFiles([] as FileData[]);
      getUserFiles(corporateId, setLoading, uuid, setUserFiles);
      setPath([]);
      setIsPrivate(false);
      setBaseFolder('Home');
    }
  }, []);

  const createNewFolder = async () => {
    let userTypeId;
    if (
      role === userTypes.corporateAdmin ||
      role === userTypes.corporateStaff
    ) {
      userTypeId = corporateId;
    } else {
      userTypeId = selectedOption;
    }
    await handleCreateNewFolder({
      newFolderName,
      setLoading,
      isPrivate,
      uuid: uuid as string,
      selectedOption: userTypeId,
      baseFolder,
      newFolderPath,
      popupNotification,
      getUserFiles,
      setUserFiles,
      setNewFolderName,
      setShowModal,
      setIsPrivate,
      setPath,
      isCorporate,
    });
  };

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles(files); // Use the hook to set selected files
  };
  useEffect(() => {
    getPrivateSharedFiles(
      setLoading,
      setIsPrivate,
      setUserFiles,
      setPath,
      isCorporate
    );
  }, []);

  return (
    <>
      <Box width="100%" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
        {showUserSelection ? (
          <Grid
            container
            spacing={2}
            p={2}
            sx={{
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              borderRadius: 4,
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                Which user type would you like to access?
              </Typography>
            </Grid>

            <Grid item xs={4}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border:
                    selectedType === 'individual'
                      ? '2px solid #1976d2'
                      : '1px solid #e0e0e0',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor:
                      selectedType === 'individual' ? '#1976d2' : '#bdbdbd',
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                  opacity: loading.loadingUsers ? 0.6 : 1,
                  height: '100%',
                }}
                onClick={() => {
                  if (loading.loadingUsers) return;
                  getUsersByAction(
                    'GetIndividual',
                    setLoading,
                    uuid,
                    employeeId,
                    setUsers
                  );
                  setSelectedType('individual');
                  setValue('option', '');
                  setUserFiles([]);
                  setPath([]);
                  setIsPrivate(false);
                  setBaseFolder('Home');
                }}
              >
                <CardContent
                  sx={{
                    padding: '12px !important',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    height: '100%',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 3,
                        background:
                          selectedType === 'individual'
                            ? 'linear-gradient(135deg, hsl(221 83% 53%), hsl(262 83% 58%))'
                            : '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          color:
                            selectedType === 'individual' ? 'white' : '#757575',
                          fontSize: '1.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <LuUser size={24} />
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color:
                            selectedType === 'individual'
                              ? '#1976d2'
                              : 'text.primary',
                          fontSize: '0.75rem',
                          lineHeight: 1.2,
                        }}
                      >
                        Individual
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.625rem', lineHeight: 1.2 }}
                      >
                        Personal account access
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border:
                        selectedType === 'individual'
                          ? '2px solid #1976d2'
                          : '2px solid #e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {selectedType === 'individual' && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: '#1976d2',
                        }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={4}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border:
                    selectedType === 'corporate'
                      ? '2px solid #1976d2'
                      : '1px solid #e0e0e0',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor:
                      selectedType === 'corporate' ? '#1976d2' : '#bdbdbd',
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                  opacity: loading.loadingUsers ? 0.6 : 1,
                  height: '100%',
                }}
                onClick={() => {
                  if (loading.loadingUsers) return;
                  getUsersByAction(
                    'GetCorporate',
                    setLoading,
                    uuid,
                    employeeId,
                    setUsers
                  );
                  setSelectedType('corporate');
                  setValue('option', '');
                  setUserFiles([]);
                  setPath([]);
                  setIsPrivate(false);
                  setBaseFolder('Home');
                }}
              >
                <CardContent
                  sx={{
                    padding: '12px !important',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    height: '100%',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 3,
                        background:
                          selectedType === 'corporate'
                            ? 'linear-gradient(135deg, hsl(221 83% 53%), hsl(262 83% 58%))'
                            : '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <BusinessIcon
                        sx={{
                          color:
                            selectedType === 'corporate' ? 'white' : '#757575',
                          fontSize: 20,
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color:
                            selectedType === 'corporate'
                              ? '#1976d2'
                              : 'text.primary',
                          fontSize: '0.75rem',
                          lineHeight: 1.2,
                        }}
                      >
                        Corporate
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.625rem', lineHeight: 1.2 }}
                      >
                        Business account access
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border:
                        selectedType === 'corporate'
                          ? '2px solid #1976d2'
                          : '2px solid #e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {selectedType === 'corporate' && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: '#1976d2',
                        }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {selectedType && (
              <Grid item xs={4}>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  <CustomDropdown
                    name="option"
                    control={control}
                    options={users}
                    label={
                      selectedType === 'individual'
                        ? 'Select user'
                        : 'Select corporate'
                    }
                    labelKey="name"
                    valueKey="id"
                    error={errors.option}
                    loading={loading.loadingUsers}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        ) : null}
        <Box display="flex" justifyContent="space-between" mt={4}>
          <Box>
            <Tooltip
              arrow
              title="This is a common area for staff to collaborate"
            >
              <Button
                size="small"
                variant={isPrivate ? 'contained' : 'outlined'}
                onClick={() => {
                  getPrivateSharedFiles(
                    setLoading,
                    setIsPrivate,
                    setUserFiles,
                    setPath,
                    isCorporate
                  );
                  setBaseFolder('private-shared');
                }}
              >
                {' '}
                Work area
              </Button>
            </Tooltip>
            <Button
              sx={{ marginLeft: 4 }}
              size="small"
              disabled={
                !selectedOption &&
                (role === userTypes.companyAdmin ||
                  role === userTypes.companyStaff)
              }
              variant={isPrivate ? 'outlined' : 'contained'}
              onClick={() => {
                setIsPrivate(false);
                getUserFiles(
                  selectedOption || corporateId, //if company admin or staff then use from drop-down else use corporateId instead
                  setLoading,
                  uuid,
                  setUserFiles
                );
                setBaseFolder('Home');
              }}
            >
              {isCorporate ? 'Collaborate' : 'View individual/corporate space'}
            </Button>
          </Box>
          <Box>
            <CustomIconButton
              tooltipTitle="Create folder"
              buttonType="primary"
              size="small"
              onClick={() => setShowModal(true)}
            >
              <CreateNewFolderIcon />
            </CustomIconButton>
            <CustomIconButton
              tooltipTitle="Upload file"
              buttonType="primary"
              size="small"
              onClick={() => setModalOpen(true)}
            >
              <UploadFileIcon />
            </CustomIconButton>
          </Box>
        </Box>

        <FileUploadModal
          open={isModalOpen}
          onClose={() => {
            setModalOpen(false);
            clearFiles(); // Clear files when modal closes
          }}
          onFilesSelected={handleFilesSelected}
          progress={progress}
          onUpload={isPrivate ? handlePrivateFileUpload : handleUpload}
          uploading={isUploading} // Use uploading state from the hook
          files={files} // Use files state from the hook
        />
        <FileTable
          userFiles={userFiles}
          path={path}
          setPath={setPath}
          getFileVersions={getFileVersions}
          loading={loading.fetchingFiles}
          isPrivate={isPrivate}
          userRole={role || undefined}
          currentUserId={selectedOption || corporateId || undefined}
          onRefreshFiles={() => {
            if (isPrivate) {
              getPrivateSharedFiles(
                setLoading,
                setIsPrivate,
                setUserFiles,
                setPath,
                isCorporate
              );
            } else {
              getUserFiles(
                selectedOption || corporateId,
                setLoading,
                uuid,
                setUserFiles
              );
            }
          }}
        />
      </Box>

      <CustomModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setNewFolderName('');
        }}
        type="info"
        autoClose={false}
        primaryButtonLoading={loading.creatingFolder}
        title="Create new folder"
        primaryButtonLabel="Ok"
        secondaryButtonAction={() => {
          setShowModal(false);
          setNewFolderName('');
        }}
        secondaryButtonLabel="Cancel"
        primaryButtonAction={createNewFolder}
      >
        <TextField
          fullWidth
          label="Folder Name"
          autoFocus
          required
          size="small"
          variant="outlined"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
        />
        {!folderNameRegex.test(newFolderName) && !!newFolderName ? (
          <FormHelperText error>
            Folder name can contain alphabets, digits, and - _ & symbols
          </FormHelperText>
        ) : (
          ''
        )}
      </CustomModal>

      <PreviousVersionFileComp
        folderId={selectedOption}
        setPreviousFileVersions={setPreviousFileVersions}
        showFileVersionModal={showFileVersionModal}
        setShowFileVersionModal={setShowFileVersionModal}
        loading={loading.fetchingFileVersion}
        previousFileVersions={previousFileVersions}
        fileName={selectedFileForVersion?.name || ''}
      />

      <TrialPeriodModal open={showTrialModal} onClose={closeTrialModal} />
    </>
  );
};

export default AdminDashboard;
